import {PhotosUploadModalWrapper} from '@features/photos-upload/components/photos-upload-modal-wrapper'
import {ModalBody, ModalFooter} from '@components/ui/modal-atoms'
import {ProgressBar} from '@components/ui/progress-bar'
import {useTranslation} from '@/translations/i18n.tsx'
import {usePhotosUpload} from '@features/photos-upload/store.ts'
import {FC, useEffect, useMemo} from 'react'
import {FilesList} from '@features/photos-upload/components/files-list'
import {ScrollArea, Scrollbar} from '@components/ui/scroll-area'
import {UploadStepFileRow} from '@features/photos-upload/components/upload-step-file-row'
import {raise} from '@utilities/helpers.ts'
import {
    httpConfirmUploadImage,
    httpGetPresignBulkImages,
    httpUploadImage
} from '@features/photos-upload/http/photos-upload.http.ts'
import {Media} from '@features/photos-upload/types.ts'
import {z} from 'zod'
import {Button} from '@components/ui/button'

export const UploadStep: FC<{openSubmitModal: () => void}> = ({openSubmitModal}) => {
    const {t} = useTranslation()
    const assignmentId = usePhotosUpload(store => store.assignmentId)
    const uploadingFiles = usePhotosUpload(store => store.uploadingFiles)
    const dropzoneFiles = usePhotosUpload(store => store.dropzoneFiles)
    const uploadedFiles = usePhotosUpload(store => store.uploadedFiles)
    const step = usePhotosUpload(store => store.step)
    const setStep = usePhotosUpload(store => store.setStep)
    const setIsOpen = usePhotosUpload(store => store.setIsOpen)
    const removeDropzoneFiles = usePhotosUpload(store => store.removeDropzoneFiles)
    const addUploadedFile = usePhotosUpload(store => store.addUploadedFile)
    const addUploadingFile = usePhotosUpload(store => store.addUploadingFile)
    const removeUploadingFile = usePhotosUpload(store => store.removeUploadingFile)
    const setUploadingFileProgressValue = usePhotosUpload(store => store.setUploadingFileProgressValue)
    const setUploadingFileIsError = usePhotosUpload(store => store.setUploadingFileIsError)
    const filesQueue: File[][] = useMemo(() => {
        const firstGroupSize = 5
        const othersGroupSize = 3
        let queue = [dropzoneFiles.slice(0, firstGroupSize)]

        for (let index = firstGroupSize; index < dropzoneFiles.length; index += othersGroupSize) {
            queue = queue.concat([dropzoneFiles.slice(index, index + othersGroupSize)])
        }

        return queue
    }, [])
    const uploadedCount = filesQueue.flat().length - dropzoneFiles.length
    const totalCount = filesQueue.flat().length

    const uploadFile = async (url: string, media: Media, file: File, uploadNextGroup: () => void) => {
        addUploadingFile(file)
        try {
            await httpUploadImage({
                url,
                payload: file,
                onUploadProgress: progressEvent =>
                    setUploadingFileProgressValue(
                        file.name,
                        progressEvent.total ? Math.round((progressEvent.loaded * 100) / progressEvent.total) : 100
                    )
            })
            await httpConfirmUploadImage({
                urlParams: {
                    assignmentId: assignmentId ?? raise('assignmentId is nullish'),
                    mediaId: media.id ?? raise('mediaId is nullish')
                }
            })
            removeDropzoneFiles([file.name])
            addUploadedFile({id: media.id, name: media.name})
            removeUploadingFile(file)
        } catch (error) {
            setUploadingFileProgressValue(file.name, 0)
            setUploadingFileIsError(file.name, true)
        } finally {
            uploadNextGroup()
        }
    }

    const uploadFiles = async () => {
        let filesQueueIndex = 0

        const uploadFilesGroup = async (files: File[]) => {
            try {
                const response = await httpGetPresignBulkImages({
                    urlParams: {assignmentId: assignmentId ?? raise('assignmentId is nullish')},
                    payload: {medias: files.map(file => file.name)}
                })
                const parsedResponseData = z
                    .array(z.object({media: Media, presigned_url: z.string().url()}))
                    .parse(response.data)

                for (const [index, item] of Object.entries(parsedResponseData)) {
                    void uploadFile(item.presigned_url, item.media, files[Number(index)], () => {
                        const nextFilesQueue = filesQueue[++filesQueueIndex]
                        if (nextFilesQueue) {
                            uploadFilesGroup(nextFilesQueue)
                        } else {
                            setStep('uploaded')
                        }
                    })
                }
            } catch (error) {
                console.error(error)
            }
        }

        await uploadFilesGroup(filesQueue[0])
    }

    useEffect(() => {
        void uploadFiles()
    }, [])

    return (
        <PhotosUploadModalWrapper>
            <ScrollArea scrollbar={<Scrollbar />}>
                <ModalBody>
                    <div>
                        <h2 style={{fontWeight: 500}}>
                            {t('single_shooting:uploading_n_assets', {uploadedCount, totalCount})}
                        </h2>
                        <ProgressBar value={totalCount ? Math.ceil((uploadedCount / totalCount) * 100) : 100} />
                    </div>
                    {dropzoneFiles.length >= 1 && (
                        <FilesList
                            title={`${t('single_shooting:to_be_uploaded')} (${dropzoneFiles.length})`}
                            data={dropzoneFiles}
                            dataItem={(_, file) => {
                                const uploadingFile = uploadingFiles.find(
                                    uploadingFile => uploadingFile.media.name == file.name
                                )
                                const status = !uploadingFile ? 'idle' : uploadingFile.isError ? 'error' : 'uploading'

                                return (
                                    <UploadStepFileRow
                                        status={status}
                                        progressValue={uploadingFile?.progressValue ?? 0}
                                        fileName={file.name}
                                    />
                                )
                            }}
                        />
                    )}
                    {uploadedFiles.length >= 1 && (
                        <FilesList
                            title={`${t('single_shooting:already_uploaded')} (${uploadedFiles.length})`}
                            data={uploadedFiles}
                            dataItem={(_, file) => <UploadStepFileRow status="uploaded" fileName={file.name} />}
                        />
                    )}
                </ModalBody>
            </ScrollArea>
            <ModalFooter>
                <Button variant="tertiary" onClick={() => setIsOpen(false)} disabled={step == 'upload'}>
                    {t('commons:cancel')}
                </Button>
                <Button
                    disabled={step == 'upload' || uploadingFiles.length > 0}
                    onClick={() => {
                        setIsOpen(false)
                        openSubmitModal()
                    }}
                >
                    {t('commons:submit')}
                </Button>
            </ModalFooter>
        </PhotosUploadModalWrapper>
    )
}
